'use client';

import { ReactNode, useState, useRef, useEffect, useCallback, useId } from 'react';
import { twMerge } from 'tailwind-merge';
import { useClickOutside } from '@app/lib/click-outside/useClickOutside';
import { useTranslations } from 'next-intl';

interface Props {
    content: ReactNode;
    children: ReactNode;
    className?: string;
    popoverClassName?: string;
}

export function InformationPopover({ content, children, className, popoverClassName }: Props) {
    const [isVisible, setIsVisible] = useState(false);
    const [position, setPosition] = useState<{
        top?: number;
        bottom?: number;
        left?: number;
        right?: number;
        transform?: string;
    }>({});
    const hoverTimeoutRef = useRef<NodeJS.Timeout>();
    const hideTimeoutRef = useRef<NodeJS.Timeout>();
    const triggerRef = useRef<HTMLDivElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);
    const popoverId = useId();
    const t = useTranslations();

    const handleClose = useCallback(() => {
        if (!isVisible) return;
        setIsVisible(false);
    }, [isVisible]);

    useClickOutside(handleClose, [triggerRef, popoverRef]);

    const calculatePosition = useCallback(() => {
        if (!triggerRef.current || !isVisible) return;

        const triggerRect = triggerRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const margin = 16;

        const newPosition: { top?: number; bottom?: number; left?: number; right?: number; transform?: string } = {};

        const centerX = triggerRect.left + triggerRect.width / 2;
        const maxPopoverWidth = Math.min(512, viewportWidth - margin * 2);

        const leftIfCentered = centerX - maxPopoverWidth / 2;
        const rightIfCentered = centerX + maxPopoverWidth / 2;

        if (leftIfCentered >= margin && rightIfCentered <= viewportWidth - margin) {
            newPosition.left = centerX;
            newPosition.transform = 'translateX(-50%)';
        } else if (leftIfCentered < margin) {
            newPosition.left = margin;
        } else {
            newPosition.right = margin;
        }

        const spaceAbove = triggerRect.top;
        const spaceBelow = viewportHeight - triggerRect.bottom;
        const minRequiredSpace = 200 + margin;

        const shouldPositionAbove =
            spaceAbove >= minRequiredSpace || (spaceBelow < minRequiredSpace && spaceAbove > spaceBelow);

        if (shouldPositionAbove) {
            newPosition.bottom = viewportHeight - triggerRect.top + 8;
        } else {
            newPosition.top = triggerRect.bottom + 8;
        }

        setPosition(newPosition);
    }, [isVisible]);

    const handleMouseEnter = () => {
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = undefined;
        }

        hoverTimeoutRef.current = setTimeout(() => {
            setIsVisible(true);
        }, 200);
    };

    const handleMouseLeave = () => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = undefined;
        }

        hideTimeoutRef.current = setTimeout(() => {
            setIsVisible(false);
        }, 100);
    };

    const handleEnterClick = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsVisible(prev => !prev);
        }
    };

    const handleFocusEnter = () => {
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = undefined;
        }
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = undefined;
        }
        setIsVisible(true);
    };

    const handleFocusOut = (e: React.FocusEvent) => {
        const nextTarget = e.relatedTarget;

        if (nextTarget && (triggerRef.current?.contains(nextTarget) || popoverRef.current?.contains(nextTarget))) {
            return;
        }

        handleMouseLeave();
    };

    const handleEscape = useCallback((e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            setIsVisible(false);
            triggerRef.current?.focus();
        }
    }, []);

    useEffect(() => {
        if (isVisible) {
            calculatePosition();

            const handleResize = () => calculatePosition();
            const handleScroll = () => calculatePosition();

            window.addEventListener('resize', handleResize);
            window.addEventListener('scroll', handleScroll, true);
            document.addEventListener('keydown', handleEscape);

            return () => {
                window.removeEventListener('resize', handleResize);
                window.removeEventListener('scroll', handleScroll, true);
                document.removeEventListener('keydown', handleEscape);
            };
        }
    }, [isVisible, calculatePosition, handleEscape]);

    const positionStyles = {
        top: position.top ? `${position.top}px` : undefined,
        bottom: position.bottom ? `${position.bottom}px` : undefined,
        left: position.left ? `${position.left}px` : undefined,
        right: position.right ? `${position.right}px` : undefined,
        transform: position.transform || undefined,
    };

    return (
        <div className={twMerge('relative inline-block', className)}>
            <div
                ref={triggerRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onFocus={handleFocusEnter}
                onBlur={handleFocusOut}
                onKeyDown={handleEnterClick}
                tabIndex={0}
                role="button"
                aria-label={t('general.showInformation')}
                aria-describedby={isVisible ? popoverId : undefined}
                aria-expanded={isVisible}
                className="cursor-help">
                {children}
            </div>

            <div
                ref={popoverRef}
                id={popoverId}
                className={twMerge(
                    'fixed z-10 flex flex-col',
                    'min-w-64 max-w-[32rem] break-words',
                    'rounded-lg bg-white shadow',
                    'p-4 text-sm leading-relaxed text-neutral-700',
                    'transition-opacity duration-150 ease-in-out',
                    !isVisible && 'hidden',
                    popoverClassName,
                )}
                style={positionStyles}
                role="tooltip"
                aria-live="polite"
                aria-hidden={!isVisible}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onBlur={handleFocusOut}>
                {content}
            </div>
        </div>
    );
}
